import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson, assetUrl } from "../utils/api";
import useSeo from "../hooks/useSeo";
import useUserMaritalStatus from "../hooks/useUserMaritalStatus";
import RelationshipFeedbackForm from "../components/RelationshipFeedbackForm";
import AiModuleTabs from "../components/AiModuleTabs";

const PHASE = {
  HERO: "hero",
  PICK: "pick",
  PORTRAIT: "portrait",
  SCENES: "scenes",
  PLAY: "play",
  REPORT: "report",
  HISTORY: "history",
  JOINT: "joint",
  EXP_PLAY: "exp_play",
  EXP_REPORT: "exp_report",
};

function MapLevel({ level }) {
  const map = {
    high: { text: "共识较高", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    medium: { text: "值得了解", cls: "bg-yellow-50 text-yellow-900 border-yellow-200" },
    watch: { text: "重点讨论", cls: "bg-amber-50 text-amber-900 border-amber-200" },
  };
  const m = map[level] || map.medium;
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${m.cls}`}>
      {m.text}
    </span>
  );
}

function SoftBar({ item }) {
  const pct = Math.round((Number(item.ratio) || 0.5) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-gray-600">
        <span>{item.label}</span>
        <span className="font-medium text-violet-700">{item.level}</span>
      </div>
      <div className="h-2 rounded-full bg-violet-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-sky-400 to-violet-500 transition-all"
          style={{ width: `${Math.min(100, Math.max(12, pct))}%` }}
        />
      </div>
    </div>
  );
}

function ZoneCard({ zone }) {
  const tone =
    zone.level === "green"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : zone.level === "orange"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-yellow-200 bg-yellow-50 text-yellow-900";
  const mark =
    zone.level === "green" ? "🟢" : zone.level === "orange" ? "🟠" : "🟡";
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <p className="text-sm font-semibold">
        {mark} {zone.title}
      </p>
      <p className="text-xs mt-1 leading-relaxed opacity-90">{zone.desc}</p>
    </div>
  );
}

const RADAR_KEYS = [
  "trust",
  "intimacy",
  "communication",
  "conflict",
  "security",
  "satisfaction",
];

/** 实验前后状态雷达（SVG），基于 report.state_curve */
function StateRadarCompare({ curve = [] }) {
  const items = RADAR_KEYS.map((key) => {
    const row = curve.find((s) => s.key === key);
    return (
      row || {
        key,
        label: key,
        from: 50,
        to: 50,
        delta: 0,
      }
    );
  }).filter((s) => curve.some((c) => c.key === s.key));

  if (items.length < 3) return null;

  const n = items.length;
  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const maxR = 78;

  const pointAt = (i, value) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    const r = (Math.min(100, Math.max(0, Number(value) || 0)) / 100) * maxR;
    return [cx + r * Math.cos(angle), cy + r * Math.sin(angle)];
  };

  const poly = (getter) =>
    items
      .map((_, i) => pointAt(i, getter(items[i])).join(","))
      .join(" ");

  const beforePoly = poly((s) => s.from);
  const afterPoly = poly((s) => s.to);

  return (
    <div className="bg-white rounded-2xl border p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-gray-800">关系状态 · 前后对比</h3>
        <div className="flex items-center gap-3 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-slate-300 inline-block" />
            实验前
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm bg-violet-500 inline-block" />
            实验后
          </span>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          className="w-52 h-52 shrink-0"
          role="img"
          aria-label="关系状态雷达对比"
        >
          {[0.25, 0.5, 0.75, 1].map((t) => (
            <polygon
              key={t}
              points={items
                .map((_, i) => {
                  const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
                  const r = maxR * t;
                  return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
                })
                .join(" ")}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          ))}
          {items.map((_, i) => {
            const [x, y] = pointAt(i, 100);
            return (
              <line
                key={i}
                x1={cx}
                y1={cy}
                x2={x}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
              />
            );
          })}
          <polygon
            points={beforePoly}
            fill="rgba(148,163,184,0.25)"
            stroke="#94a3b8"
            strokeWidth="1.5"
          />
          <polygon
            points={afterPoly}
            fill="rgba(139,92,246,0.22)"
            stroke="#7c3aed"
            strokeWidth="2"
          />
          {items.map((s, i) => {
            const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
            const lx = cx + (maxR + 18) * Math.cos(angle);
            const ly = cy + (maxR + 18) * Math.sin(angle);
            return (
              <text
                key={s.key}
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-gray-600"
                style={{ fontSize: 10 }}
              >
                {s.label}
              </text>
            );
          })}
        </svg>
        <ul className="flex-1 w-full space-y-1.5 text-xs">
          {items.map((s) => (
            <li
              key={s.key}
              className="flex justify-between gap-2 border-b border-gray-50 pb-1"
            >
              <span className="text-gray-600">{s.label}</span>
              <span className="font-medium text-violet-800 tabular-nums">
                {s.from} → {s.to}
                <span
                  className={
                    s.delta > 0
                      ? "text-emerald-600 ml-1"
                      : s.delta < 0
                        ? "text-amber-700 ml-1"
                        : "text-gray-400 ml-1"
                  }
                >
                  ({s.delta > 0 ? `+${s.delta}` : s.delta})
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="text-[11px] text-gray-400">
        数值来自本次实验中的互动轨迹，不是婚姻预测。
      </p>
    </div>
  );
}

export default function AiSimulationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = Number(localStorage.getItem("user_id"));
  const targetFromQuery = searchParams.get("target");
  const jointFromQuery = searchParams.get("joint");
  const labFromQuery = searchParams.get("lab");
  const { isMarried, loading: maritalLoading } = useUserMaritalStatus();

  const [phase, setPhase] = useState(PHASE.HERO);
  const [meta, setMeta] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [coreScenarios, setCoreScenarios] = useState([]);
  const [moreScenarios, setMoreScenarios] = useState([]);
  const [showMoreScenes, setShowMoreScenes] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [targetId, setTargetId] = useState(targetFromQuery || "");
  const [manualId, setManualId] = useState("");
  const [pickTab, setPickTab] = useState("recommended");
  const [portrait, setPortrait] = useState(null);
  const [scenarioKey, setScenarioKey] = useState("first_conflict");
  const [simId, setSimId] = useState(null);
  const [turn, setTurn] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [report, setReport] = useState(null);
  const [story, setStory] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [consentChecked, setConsentChecked] = useState(true);
  const [customQuery, setCustomQuery] = useState("");
  const [customAnswer, setCustomAnswer] = useState("");
  const [progress, setProgress] = useState({ step: 1, total: 6 });
  const [historyItems, setHistoryItems] = useState([]);
  const [relMap, setRelMap] = useState(null);
  const [friendItems, setFriendItems] = useState([]);
  const [jointSession, setJointSession] = useState(null);
  const [jointList, setJointList] = useState([]);
  const [pairMemory, setPairMemory] = useState(null);
  const [jointCustomAns, setJointCustomAns] = useState({});
  const [shareCopied, setShareCopied] = useState(false);
  const [showJointComposer, setShowJointComposer] = useState(false);
  const [jointMode, setJointMode] = useState("preset"); // preset | custom_qs
  const [jointMessage, setJointMessage] = useState("");
  const [customQs, setCustomQs] = useState([
    { text: "", choices: ["", "", ""] },
    { text: "", choices: ["", "", ""] },
  ]);
  const [sceneTab, setSceneTab] = useState("lab"); // lab | experiment
  const [expCategories, setExpCategories] = useState([]);
  const [expScenarios, setExpScenarios] = useState([]);
  const [expCategory, setExpCategory] = useState("");
  const [expKey, setExpKey] = useState("money_budget_20k");
  const [expId, setExpId] = useState(null);
  const [expRound, setExpRound] = useState(null);
  const [expRoundResult, setExpRoundResult] = useState(null);
  const [expAnswers, setExpAnswers] = useState([]);
  const [expCustom, setExpCustom] = useState("");
  const [expReport, setExpReport] = useState(null);
  const [expScenario, setExpScenario] = useState(null);
  const [expNote, setExpNote] = useState("");

  useSeo({
    title: "关系模拟实验室｜AI婚恋关系模拟｜看看你们在一起会发生什么",
    metaDescription:
      "AI关系模拟实验室，通过真实婚恋场景模拟，帮助你了解双方性格、价值观、沟通方式与潜在冲突。",
    keywords:
      "AI婚恋,关系模拟,恋爱模拟,婚恋匹配,AI婚恋匹配,恋爱关系测试,婚姻关系",
    path: "/ai/simulation",
  });

  useEffect(() => {
    if (!userId) {
      navigate(`/login?redirect=${encodeURIComponent("/ai/simulation")}`, {
        replace: true,
      });
      return;
    }
    if (!maritalLoading && isMarried) {
      navigate("/ai/marriage", { replace: true });
    }
  }, [userId, navigate, isMarried, maritalLoading]);

  const loadBase = useCallback(async () => {
    if (!userId || maritalLoading || isMarried) return;
    setLoading(true);
    setError("");
    try {
      const [metaRes, recRes, inRes, friendsRes] = await Promise.all([
        apiJson("/api/ai/simulation/lab/meta"),
        apiJson(`/api/ai/match/recommendations?user_id=${userId}&limit=8`),
        apiJson(`/api/ai/simulation/consent/inbox?user_id=${userId}`),
        apiJson(`/api/friends?current_user_id=${userId}`),
      ]);
      if (metaRes.res.ok) {
        setMeta(metaRes.data);
        const all = metaRes.data.scenarios || [];
        setScenarios(all);
        setCoreScenarios(metaRes.data.core_scenarios || all.filter((s) => s.v33_core));
        setMoreScenarios(
          metaRes.data.more_scenarios || all.filter((s) => !s.v33_core)
        );
        const cores = metaRes.data.core_scenarios || all.filter((s) => s.v33_core);
        if (cores[0]?.key) setScenarioKey(cores[0].key);
        const lib = metaRes.data.experiment_library || {};
        if (lib.categories) setExpCategories(lib.categories);
        if (lib.featured?.length) {
          setExpScenarios(lib.featured);
          if (lib.featured[0]?.key) setExpKey(lib.featured[0].key);
        }
      }
      if (recRes.res.ok) {
        const items = recRes.data.items || [];
        setRecommendations(items);
        if (!targetId && items[0]?.user?.id) {
          setTargetId(String(items[0].user.id));
        }
      }
      if (friendsRes.res.ok) {
        const list = Array.isArray(friendsRes.data)
          ? friendsRes.data
          : friendsRes.data.items || [];
        setFriendItems(list);
      }
      if (inRes.res.ok) setInbox(inRes.data.items || []);
    } catch (err) {
      setError(err.message || "加载失败");
    } finally {
      setLoading(false);
    }
  }, [userId, maritalLoading, isMarried, targetId]);

  useEffect(() => {
    loadBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isMarried, maritalLoading]);

  useEffect(() => {
    if (targetFromQuery) setTargetId(String(targetFromQuery));
  }, [targetFromQuery]);

  const openJoint = useCallback(
    async (token) => {
      if (!userId || !token) return;
      setBusy(true);
      setError("");
      try {
        const { res, data } = await apiJson(
          `/api/ai/simulation/lab/joint/${encodeURIComponent(
            token
          )}?user_id=${userId}`
        );
        if (!res.ok) throw new Error(data?.detail || "无法打开共同实验");
        setJointSession(data);
        if (data.invitee?.id && data.role === "inviter") {
          setTargetId(String(data.invitee.id));
        } else if (data.inviter?.id && data.role === "invitee") {
          setTargetId(String(data.inviter.id));
        }
        setPhase(PHASE.JOINT);
      } catch (err) {
        setError(err.message || "打开失败");
        alert(err.message || "打开共同实验失败");
      } finally {
        setBusy(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (!userId || maritalLoading || isMarried) return;
    if (jointFromQuery) openJoint(jointFromQuery);
  }, [userId, maritalLoading, isMarried, jointFromQuery, openJoint]);

  const selectedRec = useMemo(
    () => recommendations.find((x) => String(x.user?.id) === String(targetId)),
    [recommendations, targetId]
  );

  const inviteJoint = async ({ withCustomQuestions = false } = {}) => {
    const tid = Number(targetId);
    if (!tid) {
      alert("请先选择对象");
      return;
    }
    let custom_questions = null;
    if (withCustomQuestions || jointMode === "custom_qs") {
      const prepared = customQs
        .map((q) => {
          const text = (q.text || "").trim();
          if (!text) return null;
          const choices = (q.choices || [])
            .map((c) => (c || "").trim())
            .filter(Boolean);
          return choices.length >= 2
            ? { text, choices }
            : { text };
        })
        .filter(Boolean);
      if (prepared.length < 2) {
        alert("自定义题目至少填写 2 道");
        return;
      }
      custom_questions = prepared;
    }
    setBusy(true);
    try {
      const { res, data } = await apiJson("/api/ai/simulation/lab/joint/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          target_user_id: tid,
          scenario_key:
            sceneTab === "experiment" && expKey
              ? expKey
              : scenarioKey || "parents",
          message:
            (jointMessage || "").trim() ||
            "想和你一起做一场关系实验：我们各自先独立作答，再看 AI 对比。",
          custom_query: custom_questions
            ? (customQs[0]?.text || "").trim().slice(0, 80)
            : "",
          custom_questions,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "邀请失败");
      setJointSession(data);
      setShowJointComposer(false);
      setPhase(PHASE.JOINT);
    } catch (err) {
      alert(err.message || "邀请失败");
    } finally {
      setBusy(false);
    }
  };

  const acceptJoint = async () => {
    if (!jointSession?.share_token) return;
    setBusy(true);
    try {
      const { res, data } = await apiJson("/api/ai/simulation/lab/joint/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          token: jointSession.share_token,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "接受失败");
      setJointSession(data);
    } catch (err) {
      alert(err.message || "接受失败");
    } finally {
      setBusy(false);
    }
  };

  const declineJoint = async () => {
    if (!jointSession?.share_token) return;
    setBusy(true);
    try {
      const { res, data } = await apiJson("/api/ai/simulation/lab/joint/decline", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          token: jointSession.share_token,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "拒绝失败");
      setJointSession(null);
      setPhase(PHASE.HERO);
    } catch (err) {
      alert(err.message || "拒绝失败");
    } finally {
      setBusy(false);
    }
  };

  const answerJoint = async (questionId, choiceKey = "", freeText = "") => {
    if (!jointSession?.share_token || busy) return;
    setBusy(true);
    try {
      const { res, data } = await apiJson("/api/ai/simulation/lab/joint/answer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          token: jointSession.share_token,
          question_id: questionId,
          choice_key: freeText ? "" : choiceKey,
          custom_response: freeText || "",
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "提交失败");
      setJointSession(data);
      setJointCustomAns((prev) => ({ ...prev, [questionId]: "" }));
    } catch (err) {
      alert(err.message || "提交失败");
    } finally {
      setBusy(false);
    }
  };

  const copyJointLink = async () => {
    const path = jointSession?.share_path || "";
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      prompt("复制分享链接：", url);
    }
  };

  const loadPortrait = async () => {
    const tid = Number(targetId || manualId);
    if (!tid) {
      alert("请先选择一位对象");
      return;
    }
    setTargetId(String(tid));
    setBusy(true);
    setError("");
    try {
      const { res, data } = await apiJson(
        `/api/ai/simulation/lab/portrait?user_id=${userId}&target_user_id=${tid}`
      );
      if (!res.ok) throw new Error(data?.detail || "画像加载失败");
      setPortrait(data);
      setPhase(PHASE.PORTRAIT);
    } catch (err) {
      setError(err.message || "画像加载失败");
      alert(err.message || "画像加载失败");
    } finally {
      setBusy(false);
    }
  };

  const startLab = async ({ random = false, custom = "" } = {}) => {
    const tid = Number(targetId);
    if (!tid) {
      alert("请先选择对象");
      return;
    }
    if (!consentChecked) {
      alert("请先确认你理解：这是关系实验，不是命运预测");
      return;
    }
    const q = (custom || customQuery || "").trim();
    setBusy(true);
    setError("");
    try {
      const { res, data } = await apiJson("/api/ai/simulation/lab/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          target_user_id: tid,
          scenario_key: scenarioKey,
          random_mode: random && !q,
          custom_query: q,
          consent: true,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "无法开始实验");
      setSimId(data.id);
      setScenario(data.scenario);
      setPortrait(data.portrait || portrait);
      setTurn(data.turn);
      setReport(null);
      setRelMap(data.portrait?.relationship_map || null);
      setProgress(data.progress || { step: 1, total: data.total_steps || 6 });
      setCustomAnswer("");
      setStory([
        {
          role: "event",
          text: data.turn?.narrative,
        },
      ]);
      setPhase(PHASE.PLAY);
    } catch (err) {
      setError(err.message || "开始失败");
      alert(err.message || "开始失败");
    } finally {
      setBusy(false);
    }
  };

  const loadExpLibrary = async (category = "") => {
    try {
      const q = category
        ? `?category=${encodeURIComponent(category)}`
        : "?featured_only=false";
      const { res, data } = await apiJson(
        `/api/ai/simulation/experiment/library${q}`
      );
      if (!res.ok) return;
      setExpCategories(data.categories || []);
      setExpScenarios(data.items || []);
      if (!category && (data.featured || []).length && !expKey) {
        setExpKey(data.featured[0].key);
      }
    } catch {
      /* ignore */
    }
  };

  const startExperiment = async (key = expKey) => {
    const tid = Number(targetId);
    if (!tid) {
      alert("请先选择对象");
      return;
    }
    if (!consentChecked) {
      alert("请先确认你理解：这是关系实验，不是命运预测");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { res, data } = await apiJson("/api/ai/simulation/experiment/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          target_user_id: tid,
          scenario_key: key,
          consent: true,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "无法开始关系实验");
      setExpId(data.id);
      setExpScenario(data.scenario);
      setExpRound(data.round);
      setExpRoundResult(null);
      setExpAnswers([]);
      setExpReport(null);
      setExpCustom("");
      setExpNote(data.partner_answer_note || "");
      setPortrait(data.portrait || portrait);
      setProgress(data.progress || { step: 1, total: data.round?.total || 3 });
      setPhase(PHASE.EXP_PLAY);
    } catch (err) {
      setError(err.message || "开始失败");
      alert(err.message || "开始失败");
    } finally {
      setBusy(false);
    }
  };

  const answerExperiment = async (choiceKey, freeText = "") => {
    if (!expId || busy) return;
    const text = (freeText || "").trim();
    if (!choiceKey && !text) {
      alert("请选择一个选项，或写下你自己的回答");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { res, data } = await apiJson("/api/ai/simulation/experiment/answer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          experiment_id: expId,
          choice_key: text ? "" : choiceKey,
          custom_response: text,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "提交失败");
      setExpRoundResult(data.round_result);
      setExpAnswers((prev) => [...prev, data.round_result].filter(Boolean));
      setExpCustom("");
      setProgress(data.progress || progress);
      if (data.done) {
        setExpReport(data.report);
        setPhase(PHASE.EXP_REPORT);
      } else {
        setExpRound(data.round);
      }
    } catch (err) {
      setError(err.message || "提交失败");
      alert(err.message || "提交失败");
    } finally {
      setBusy(false);
    }
  };

  const choose = async (key, freeText = "") => {
    if (!simId || busy) return;
    const text = (freeText || "").trim();
    if (!key && !text) {
      alert("请选择一个选项，或写下你自己的回答");
      return;
    }
    const choice = (turn?.choices || []).find(
      (c) => String(c.key).toUpperCase() === String(key).toUpperCase()
    );
    setBusy(true);
    setError("");
    try {
      setStory((prev) => [
        ...prev,
        { role: "me", text: text || choice?.text || key },
      ]);
      const { res, data } = await apiJson("/api/ai/simulation/lab/choose", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          simulation_id: simId,
          choice_key: text ? "" : key,
          custom_response: text,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "继续模拟失败");
      setProgress(data.progress || progress);
      setCustomAnswer("");
      if (data.done) {
        setReport(data.report);
        setRelMap(data.relationship_map || data.report?.relationship_map || relMap);
        setStory((prev) => [
          ...prev,
          { role: "ta", text: data.report?.pattern || "实验结束，请查看报告。" },
        ]);
        setPhase(PHASE.REPORT);
      } else {
        setTurn(data.turn);
        setStory((prev) => [
          ...prev,
          { role: "ta", text: data.turn?.partner_reaction },
          data.turn?.narrative
            ? { role: "event", text: data.turn.narrative }
            : null,
        ].filter(Boolean));
      }
    } catch (err) {
      setError(err.message || "选择失败");
      alert(err.message || "选择失败");
    } finally {
      setBusy(false);
    }
  };

  const loadHistory = async () => {
    if (!userId) return;
    setBusy(true);
    try {
      const q = targetId
        ? `&target_user_id=${Number(targetId)}`
        : "";
      const { res, data } = await apiJson(
        `/api/ai/simulation/lab/history?user_id=${userId}&limit=20${q}`
      );
      if (res.ok) setHistoryItems(data.items || []);
      const jointRes = await apiJson(
        `/api/ai/simulation/lab/joint/list?user_id=${userId}&limit=20`
      );
      if (jointRes.res.ok) setJointList(jointRes.data.items || []);
      if (targetId) {
        const mapRes = await apiJson(
          `/api/ai/simulation/lab/map?user_id=${userId}&target_user_id=${Number(
            targetId
          )}`
        );
        if (mapRes.res.ok) setRelMap(mapRes.data);
        const memRes = await apiJson(
          `/api/ai/simulation/lab/joint/memory?user_id=${userId}&target_user_id=${Number(
            targetId
          )}`
        );
        if (memRes.res.ok) setPairMemory(memRes.data);
      }
      setPhase(PHASE.HISTORY);
    } catch (err) {
      alert(err.message || "加载历史失败");
    } finally {
      setBusy(false);
    }
  };

  const resumeLab = async (id) => {
    setBusy(true);
    try {
      const { res, data } = await apiJson(
        `/api/ai/simulation/lab/${id}?user_id=${userId}`
      );
      if (!res.ok) throw new Error(data?.detail || "无法继续");
      const lab = data.lab || {};
      setSimId(id);
      setScenario(lab.scenario);
      setPortrait(lab.portrait || portrait);
      setRelMap(lab.relationship_map || lab.report?.relationship_map || null);
      setProgress({
        step: lab.step || 1,
        total: lab.total_steps || 6,
      });
      if (lab.status === "done" && lab.report) {
        setReport(lab.report);
        setPhase(PHASE.REPORT);
      } else {
        const events = lab.events || [];
        const last = events[events.length - 1];
        setTurn(last);
        setStory(
          events
            .filter((e) => e.narrative || e.partner_reaction || e.choice)
            .flatMap((e) => {
              const rows = [];
              if (e.narrative) rows.push({ role: "event", text: e.narrative });
              if (e.choice?.text) rows.push({ role: "me", text: e.choice.text });
              if (e.partner_reaction)
                rows.push({ role: "ta", text: e.partner_reaction });
              return rows;
            })
        );
        setPhase(PHASE.PLAY);
      }
    } catch (err) {
      alert(err.message || "无法继续实验");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!userId || maritalLoading || isMarried || !labFromQuery) return;
    resumeLab(Number(labFromQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, maritalLoading, isMarried, labFromQuery]);

  const retryLab = async () => {
    if (!simId) return;
    setBusy(true);
    try {
      const { res, data } = await apiJson("/api/ai/simulation/lab/retry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, simulation_id: simId }),
      });
      if (!res.ok) throw new Error(data?.detail || "无法再次模拟");
      setSimId(data.id);
      setScenario(data.scenario);
      setTurn(data.turn);
      setReport(null);
      setProgress(data.progress || { step: 1, total: 6 });
      setStory([{ role: "event", text: data.turn?.narrative }]);
      setPhase(PHASE.PLAY);
      if (data.previous_report) {
        // keep previous summary lightly in story system message
        setStory((prev) => [
          {
            role: "event",
            text: `第 ${data.attempt || 2} 次尝试：换一种方式再体验同一情境。`,
          },
          ...prev,
        ]);
      }
    } catch (err) {
      alert(err.message || "再次模拟失败");
    } finally {
      setBusy(false);
    }
  };

  const respondConsent = async (requestId, accept) => {
    try {
      const { res, data } = await apiJson("/api/ai/simulation/consent/respond", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          request_id: requestId,
          accept,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "处理失败");
      const inRes = await apiJson(
        `/api/ai/simulation/consent/inbox?user_id=${userId}`
      );
      if (inRes.res.ok) setInbox(inRes.data.items || []);
    } catch (err) {
      alert(err.message || "处理失败");
    }
  };

  const disclaimer = meta?.disclaimer ||
    "温馨提示：关系模拟基于双方当前提供的信息生成，仅用于帮助你发现彼此的差异、优势与潜在冲突，不代表对真实关系未来的预测。";

  return (
    <div className="min-h-screen bg-[#fbf7f2] flex flex-col">
      <Header />
      <main className="flex-1 w-full mx-auto px-4 py-8 max-w-5xl space-y-6">
        <AiModuleTabs groupKey="relationship" />
        {inbox.length > 0 && phase === PHASE.HERO && (
          <section className="bg-white rounded-2xl border border-amber-200 p-4 space-y-2">
            <h2 className="font-semibold text-gray-900 text-sm">待我授权的请求</h2>
            {inbox.map((item) => (
              <div
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 text-sm"
              >
                <span>
                  {item.peer?.name || "会员"} 邀请模拟
                  {item.scenario?.title ? `「${item.scenario.title}」` : ""}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => respondConsent(item.id, true)}
                    className="px-3 py-1 rounded-full bg-rose-500 text-white text-xs"
                  >
                    同意
                  </button>
                  <button
                    type="button"
                    onClick={() => respondConsent(item.id, false)}
                    className="px-3 py-1 rounded-full border text-xs"
                  >
                    拒绝
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {phase === PHASE.HERO && (
          <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-100 via-rose-50 to-sky-100 border border-white shadow-sm p-8 md:p-12 space-y-6">
            <div className="absolute right-8 top-10 hidden md:flex items-center gap-3 opacity-80">
              <div className="w-14 h-14 rounded-full bg-white/80 border border-rose-100" />
              <div className="w-24 h-0.5 bg-gradient-to-r from-emerald-400 via-amber-300 to-orange-300" />
              <div className="w-14 h-14 rounded-full bg-white/80 border border-violet-100" />
            </div>
            <p className="text-sm text-violet-600 font-medium tracking-wide">🧪 AI 关系实验室</p>
            <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight">
              关系模拟实验室
            </h1>
            <p className="text-lg text-rose-700 font-semibold">
              如果你们真的在一起，会发生什么？
            </p>
            <p className="text-sm text-gray-600 max-w-xl leading-relaxed">
              不只是看“匹不匹配”，
              <br />
              让 AI 带你们提前经历一段关系。
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPhase(PHASE.PICK)}
                className="px-6 py-3 rounded-full bg-violet-600 text-white font-semibold shadow-md hover:bg-violet-700"
              >
                开始情景模拟
              </button>
              <button
                type="button"
                onClick={() => {
                  setSceneTab("experiment");
                  loadExpLibrary("");
                  setPhase(PHASE.PICK);
                }}
                className="px-6 py-3 rounded-full border border-violet-300 bg-white text-violet-700 font-semibold"
              >
                多轮关系实验
              </button>
              <button
                type="button"
                onClick={() => {
                  setPhase(PHASE.SCENES);
                }}
                className="px-6 py-3 rounded-full border border-violet-200 bg-white/80 text-violet-700 font-medium"
              >
                看看怎么玩
              </button>
              <button
                type="button"
                onClick={loadHistory}
                className="px-6 py-3 rounded-full border border-gray-200 bg-white text-gray-600 text-sm"
              >
                我的关系实验
              </button>
              <Link
                to="/ai/relationship-growth"
                className="px-6 py-3 rounded-full border border-rose-200 bg-white text-rose-700 text-sm font-medium"
              >
                关系成长追踪
              </Link>
              <Link
                to="/ai/behavior-profile"
                className="px-6 py-3 rounded-full border border-amber-200 bg-white text-amber-800 text-sm font-medium"
              >
                我的关系行为画像
              </Link>
              <Link
                to="/ai/relationship-map"
                className="px-6 py-3 rounded-full border border-violet-200 bg-white text-violet-800 text-sm font-medium"
              >
                AI 关系地图
              </Link>
              <Link
                to="/ai/relationship-memory"
                className="px-6 py-3 rounded-full border border-violet-200 bg-white text-violet-800 text-sm font-medium"
              >
                关系记忆看板
              </Link>
            </div>
            <p className="text-xs text-violet-600/80">
              AI 不是回答一个问题，而是陪你完成一次关系实验。
            </p>
            <div className="flex flex-wrap gap-3 text-xs text-gray-500 pt-2">
              <Link to="/discover" className="hover:text-rose-600">
                发现对象
              </Link>
              <span>·</span>
              <Link to="/love-map" className="hover:text-rose-600">
                爱情地图
              </Link>
              <span>·</span>
              <Link to="/ai/growth" className="hover:text-rose-600">
                成长中心
              </Link>
            </div>
            <p className="text-[11px] text-gray-400 leading-relaxed max-w-2xl pt-4 border-t border-white/60">
              {disclaimer}
            </p>
          </section>
        )}

        {phase === PHASE.PICK && (
          <section className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <button
              type="button"
              onClick={() => setPhase(PHASE.HERO)}
              className="text-sm text-gray-500"
            >
              ← 返回
            </button>
            <h2 className="text-2xl font-bold text-gray-900">选择一个人开始实验</h2>
            <div className="flex flex-wrap gap-2">
              {[
                ["recommended", "推荐对象"],
                ["friends", "好友/已关注"],
                ["manual", "指定用户"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setPickTab(k)}
                  className={`px-4 py-2 rounded-full text-sm border ${
                    pickTab === k
                      ? "bg-violet-600 text-white border-violet-600"
                      : "bg-white text-gray-600 border-gray-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {pickTab === "recommended" && (
              <div className="grid sm:grid-cols-2 gap-3">
                {loading ? (
                  <p className="text-sm text-gray-400">加载推荐中…</p>
                ) : recommendations.length === 0 ? (
                  <p className="text-sm text-gray-500 col-span-2">
                    暂无推荐。可去{" "}
                    <Link to="/discover" className="text-rose-600">
                      寻找对象
                    </Link>{" "}
                    ，或指定用户 ID。建议先完成 AI 人格访谈以提升模拟质量。
                  </p>
                ) : (
                  recommendations.map((item) => (
                    <button
                      key={item.user.id}
                      type="button"
                      onClick={() => setTargetId(String(item.user.id))}
                      className={`flex items-center gap-3 rounded-2xl p-3 border text-left bg-white ${
                        String(item.user.id) === String(targetId)
                          ? "border-violet-400 ring-2 ring-violet-100"
                          : "border-gray-100"
                      }`}
                    >
                      <img
                        src={
                          assetUrl(item.user.avatar) ||
                          "https://picsum.photos/56"
                        }
                        alt=""
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 truncate">
                          {item.user.name}
                          {item.user.age ? ` · ${item.user.age}` : ""}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {item.user.city || "城市未填"}
                          {item.long_term_index
                            ? ` · 关系倾向参考 ${item.long_term_index}`
                            : ""}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {pickTab === "friends" && (
              <div className="grid sm:grid-cols-2 gap-3">
                {friendItems.length === 0 ? (
                  <p className="text-sm text-gray-500 col-span-2">
                    暂无好友。可去寻找对象后添加，或使用「指定用户」。
                  </p>
                ) : (
                  friendItems.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setTargetId(String(u.id))}
                      className={`flex items-center gap-3 rounded-2xl p-3 border text-left bg-white ${
                        String(u.id) === String(targetId)
                          ? "border-violet-400 ring-2 ring-violet-100"
                          : "border-gray-100"
                      }`}
                    >
                      <img
                        src={assetUrl(u.avatar) || "https://picsum.photos/56"}
                        alt=""
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 truncate">
                          {u.name}
                          {u.age ? ` · ${u.age}` : ""}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {u.city || "城市未填"}
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            {pickTab === "manual" && (
              <div className="bg-white rounded-2xl border p-4 space-y-3">
                <input
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  placeholder="输入对方用户 ID"
                  className="w-full border rounded-xl px-3 py-2.5 text-sm"
                />
                <p className="text-xs text-gray-400">
                  可从对方主页链接或匹配结果中查看用户 ID
                </p>
              </div>
            )}

            <button
              type="button"
              disabled={busy}
              onClick={loadPortrait}
              className="w-full sm:w-auto px-6 py-3 rounded-full bg-violet-600 text-white font-semibold disabled:opacity-60"
            >
              {busy ? "生成双画像…" : "进入双方关系画像"}
            </button>
          </section>
        )}

        {phase === PHASE.PORTRAIT && portrait && (
          <section className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <button
              type="button"
              onClick={() => setPhase(PHASE.PICK)}
              className="text-sm text-gray-500"
            >
              ← 重选对象
            </button>
            <h2 className="text-2xl font-bold text-gray-900">关系双画像</h2>
            <p className="text-sm text-gray-500">
              AI 关系观察 · 协同倾向：
              <span className="text-violet-700 font-semibold ml-1">
                {portrait.observation?.synergy_label || "中等"}
              </span>
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {[portrait.me, portrait.partner].map((side, idx) => (
                <div
                  key={side.id || idx}
                  className="bg-white rounded-2xl border border-violet-100 p-5 space-y-3 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={assetUrl(side.avatar) || "https://picsum.photos/48"}
                      alt=""
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-xs text-violet-500">
                        {idx === 0 ? "我" : "TA"}
                      </p>
                      <p className="font-bold text-gray-800">{side.name}</p>
                    </div>
                  </div>
                  <dl className="space-y-1.5 text-xs text-gray-600">
                    {[
                      ["性格关键词", side.traits?.personality],
                      ["核心价值观", side.traits?.values],
                      ["婚恋期待", side.traits?.marriage],
                      ["沟通风格", side.traits?.communication],
                      ["家庭观", side.traits?.family],
                      ["消费观", side.traits?.money],
                      ["生育观", side.traits?.children],
                      ["情绪模式", side.traits?.emotion],
                      ["信仰/原则", side.traits?.faith_or_principle],
                    ].map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <dt className="w-20 shrink-0 text-gray-400">{k}</dt>
                        <dd className="flex-1">{v || "信息不足"}</dd>
                      </div>
                    ))}
                  </dl>
                  {!side.profile_ready && (
                    <p className="text-[11px] text-amber-600">
                      画像信息不完整，模拟将带有更多不确定性。
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border p-5 space-y-4">
              <h3 className="font-semibold text-gray-800">🧠 AI 关系观察</h3>
              <p className="text-[11px] text-gray-400">
                不是匹配分数，而是根据双方资料与关系地图看到的结构倾向。
              </p>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-emerald-700 font-medium mb-1">🟢 容易共鸣的区域</p>
                  <ul className="space-y-2 text-gray-600 text-xs">
                    {(portrait.pair_structure?.resonate || []).map((s) => (
                      <li key={s}>· {s}</li>
                    ))}
                    {!(portrait.pair_structure?.resonate || []).length &&
                      (portrait.observation?.green || []).map((s) => (
                        <li key={s.text || s}>
                          <span className="font-medium text-gray-800">
                            · {s.text || s}
                          </span>
                          {s.why && (
                            <p className="pl-3 text-gray-400 mt-0.5">为什么：{s.why}</p>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
                <div>
                  <p className="text-yellow-800 font-medium mb-1">🟡 需要磨合的区域</p>
                  <ul className="space-y-2 text-gray-600 text-xs">
                    {(portrait.pair_structure?.need_work || []).map((s) => (
                      <li key={s}>· {s}</li>
                    ))}
                    {!(portrait.pair_structure?.need_work || []).length &&
                      (portrait.observation?.yellow || []).map((s) => (
                        <li key={s.text || s}>
                          <span className="font-medium text-gray-800">
                            · {s.text || s}
                          </span>
                          {s.why && (
                            <p className="pl-3 text-gray-400 mt-0.5">为什么：{s.why}</p>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
                <div>
                  <p className="text-amber-800 font-medium mb-1">🟠 潜在冲突 / 值得提前了解</p>
                  <ul className="space-y-2 text-gray-600 text-xs">
                    {(portrait.pair_structure?.conflict_risk || []).map((s) => (
                      <li key={s}>· {s}</li>
                    ))}
                    {(portrait.pair_structure?.learn_more || []).map((s) => (
                      <li key={`learn-${s}`}>· {s}</li>
                    ))}
                    {!(portrait.pair_structure?.conflict_risk || []).length &&
                      (portrait.observation?.orange || []).map((s) => (
                        <li key={s.text || s}>
                          <span className="font-medium text-gray-800">
                            · {s.text || s}
                          </span>
                          {s.why && (
                            <p className="pl-3 text-gray-400 mt-0.5">为什么：{s.why}</p>
                          )}
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
              <div className="space-y-2 pt-2">
                {(portrait.soft_bars || []).map((b) => (
                  <SoftBar key={b.key} item={b} />
                ))}
              </div>
              <p className="text-[11px] text-gray-400">
                {portrait.pair_structure?.note || portrait.observation?.note}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setPhase(PHASE.SCENES)}
              className="px-6 py-3 rounded-full bg-violet-600 text-white font-semibold"
            >
              选择一个关系实验
            </button>
          </section>
        )}

        {phase === PHASE.SCENES && (
          <section className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <button
              type="button"
              onClick={() =>
                setPhase(portrait ? PHASE.PORTRAIT : PHASE.PICK)
              }
              className="text-sm text-gray-500"
            >
              ← 返回
            </button>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  选择一个关系实验
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {sceneTab === "experiment"
                    ? "多轮问答：你作答 → 对照对方可能倾向 → 逐轮推进 → 实验报告"
                    : "第一期六大核心：消费 · 父母 · 孩子 · 工作 · 沟通 · 家庭分工"}
                  {selectedRec?.user?.name
                    ? ` · 与 ${selectedRec.user.name}`
                    : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || !targetId}
                  onClick={() => startLab({ random: true })}
                  className="px-4 py-2 rounded-full border border-violet-300 bg-white text-violet-700 text-sm font-medium disabled:opacity-50"
                >
                  🎲 随机情景
                </button>
                <button
                  type="button"
                  disabled={busy || !targetId}
                  onClick={() => {
                    setJointMode("preset");
                    setShowJointComposer(true);
                  }}
                  className="px-4 py-2 rounded-full border border-rose-200 bg-white text-rose-700 text-sm font-medium disabled:opacity-50"
                >
                  🤝 邀请 TA 共同实验
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSceneTab("lab")}
                className={`px-4 py-2 rounded-full text-sm border ${
                  sceneTab === "lab"
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                情景模拟
              </button>
              <button
                type="button"
                onClick={() => {
                  setSceneTab("experiment");
                  loadExpLibrary(expCategory);
                }}
                className={`px-4 py-2 rounded-full text-sm border ${
                  sceneTab === "experiment"
                    ? "bg-violet-600 text-white border-violet-600"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                多轮问答实验
              </button>
            </div>

            {showJointComposer && (
              <div className="bg-white rounded-2xl border border-rose-100 p-4 space-y-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-gray-800 text-sm">
                    创建共同实验邀请
                  </h3>
                  <button
                    type="button"
                    className="text-xs text-gray-400"
                    onClick={() => setShowJointComposer(false)}
                  >
                    关闭
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setJointMode("preset")}
                    className={`px-3 py-1.5 rounded-full text-xs border ${
                      jointMode === "preset"
                        ? "bg-violet-600 text-white border-violet-600"
                        : "bg-white text-gray-600"
                    }`}
                  >
                    用当前场景题库
                  </button>
                  <button
                    type="button"
                    onClick={() => setJointMode("custom_qs")}
                    className={`px-3 py-1.5 rounded-full text-xs border ${
                      jointMode === "custom_qs"
                        ? "bg-violet-600 text-white border-violet-600"
                        : "bg-white text-gray-600"
                    }`}
                  >
                    自定义题目
                  </button>
                </div>
                <input
                  value={jointMessage}
                  onChange={(e) => setJointMessage(e.target.value.slice(0, 200))}
                  placeholder="给 TA 的邀请留言（可选）"
                  className="w-full border rounded-xl px-3 py-2 text-sm"
                />
                {jointMode === "preset" ? (
                  <p className="text-xs text-gray-500">
                    将使用场景「
                    {scenarios.find((s) => s.key === scenarioKey)?.title ||
                      scenarioKey}
                    」的 4 道标准题，双方分别作答后 AI 对比。
                  </p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                      至少 2 道、最多 6 道。选项可留空（系统会给默认三项）。
                    </p>
                    {customQs.map((q, qi) => (
                      <div
                        key={qi}
                        className="rounded-xl border border-violet-50 bg-violet-50/30 p-3 space-y-2"
                      >
                        <input
                          value={q.text}
                          onChange={(e) => {
                            const next = [...customQs];
                            next[qi] = {
                              ...next[qi],
                              text: e.target.value.slice(0, 200),
                            };
                            setCustomQs(next);
                          }}
                          placeholder={`题目 ${qi + 1}，例如：春节只能去一方父母家，你会怎么选？`}
                          className="w-full border rounded-lg px-3 py-2 text-sm"
                        />
                        <div className="grid sm:grid-cols-3 gap-2">
                          {(q.choices || ["", "", ""]).map((c, ci) => (
                            <input
                              key={ci}
                              value={c}
                              onChange={(e) => {
                                const next = [...customQs];
                                const choices = [...(next[qi].choices || ["", "", ""])];
                                choices[ci] = e.target.value.slice(0, 80);
                                next[qi] = { ...next[qi], choices };
                                setCustomQs(next);
                              }}
                              placeholder={`选项 ${String.fromCharCode(65 + ci)}（可选）`}
                              className="border rounded-lg px-2 py-1.5 text-xs"
                            />
                          ))}
                        </div>
                        {customQs.length > 2 && (
                          <button
                            type="button"
                            className="text-[11px] text-rose-500"
                            onClick={() =>
                              setCustomQs(customQs.filter((_, i) => i !== qi))
                            }
                          >
                            删除此题
                          </button>
                        )}
                      </div>
                    ))}
                    {customQs.length < 6 && (
                      <button
                        type="button"
                        className="text-xs text-violet-700"
                        onClick={() =>
                          setCustomQs([
                            ...customQs,
                            { text: "", choices: ["", "", ""] },
                          ])
                        }
                      >
                        + 再加一题
                      </button>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  disabled={busy || !targetId}
                  onClick={() => inviteJoint()}
                  className="px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? "创建中…" : "发送邀请并开始"}
                </button>
              </div>
            )}

            {sceneTab === "lab" ? (
              <>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-violet-800">六大核心场景</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {(coreScenarios.length ? coreScenarios : scenarios).map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setScenarioKey(s.key)}
                    className={`text-left rounded-2xl border bg-white p-4 transition ${
                      scenarioKey === s.key
                        ? "border-violet-400 ring-2 ring-violet-100"
                        : "border-gray-100 hover:border-violet-200"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-xl">{s.icon || "🧪"}</span>
                      <div>
                        <p className="text-xs text-violet-500">
                          {s.focus || s.subtitle || s.category}
                        </p>
                        <p className="font-semibold text-gray-800 mt-0.5">
                          {s.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          {s.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {moreScenarios.length > 0 && (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setShowMoreScenes((v) => !v)}
                  className="text-sm text-gray-600 underline"
                >
                  {showMoreScenes ? "收起更多场景" : `更多场景（${moreScenarios.length}）`}
                </button>
                {showMoreScenes && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {moreScenarios.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setScenarioKey(s.key)}
                        className={`text-left rounded-2xl border bg-white p-4 transition ${
                          scenarioKey === s.key
                            ? "border-violet-400 ring-2 ring-violet-100"
                            : "border-gray-100 hover:border-violet-200"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-xl">{s.icon || "🧪"}</span>
                          <div>
                            <p className="text-xs text-gray-500">
                              {s.focus || s.subtitle || s.category}
                            </p>
                            <p className="font-semibold text-gray-800 mt-0.5">
                              {s.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                              {s.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label className="flex items-start gap-2 text-xs text-gray-600 bg-white rounded-xl border p-3">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                我理解这是关系实验，不是对未来的预测。信息不足时结果会有不确定性。
              </span>
            </label>

            <div className="bg-white rounded-2xl border border-violet-100 p-4 space-y-3">
              <h3 className="font-semibold text-gray-800 text-sm">✨ 自定义关系实验</h3>
              <p className="text-xs text-gray-500">
                你最想模拟什么？Agent 会根据双方资料组织一场实验。
              </p>
              <textarea
                value={customQuery}
                onChange={(e) => setCustomQuery(e.target.value.slice(0, 300))}
                rows={3}
                placeholder="例如：我想看看如果我和TA结婚以后，我创业失败了，会怎么样。"
                className="w-full border rounded-xl px-3 py-2 text-sm resize-none"
              />
              <button
                type="button"
                disabled={busy || !targetId || !customQuery.trim()}
                onClick={() => startLab({ custom: customQuery })}
                className="px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                让 Agent 开始这场自由实验
              </button>
            </div>

            <button
              type="button"
              disabled={busy || !targetId}
              onClick={() => startLab({ random: false })}
              className="w-full sm:w-auto px-6 py-3 rounded-full bg-violet-600 text-white font-semibold disabled:opacity-60"
            >
              {busy ? "正在进入情境…" : "开始这场实验"}
            </button>
              </>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setExpCategory("");
                      loadExpLibrary("");
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs border ${
                      !expCategory
                        ? "bg-violet-600 text-white border-violet-600"
                        : "bg-white text-gray-600"
                    }`}
                  >
                    全部
                  </button>
                  {expCategories.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => {
                        setExpCategory(c.key);
                        loadExpLibrary(c.key);
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs border ${
                        expCategory === c.key
                          ? "bg-violet-600 text-white border-violet-600"
                          : "bg-white text-gray-600"
                      }`}
                    >
                      {c.icon || ""} {c.label}
                      {typeof c.count === "number" ? ` (${c.count})` : ""}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  场景库共 {expScenarios.length || meta?.experiment_library?.scenario_count || 0}{" "}
                  个多轮实验。你作答后，系统会给出对方「基于资料的可能倾向」对照（非对方真实作答）。
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {expScenarios.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setExpKey(s.key)}
                      className={`text-left rounded-2xl border bg-white p-4 transition ${
                        expKey === s.key
                          ? "border-violet-400 ring-2 ring-violet-100"
                          : "border-gray-100 hover:border-violet-200"
                      }`}
                    >
                      <p className="text-xs text-violet-500">
                        {s.conflict_type || s.subtitle || s.category}
                        {s.featured ? " · 推荐" : ""}
                      </p>
                      <p className="font-semibold text-gray-800 mt-0.5">{s.title}</p>
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                        {s.description}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-2">
                        {s.round_count || 3} 轮问答 · {s.difficulty || "中"}
                      </p>
                    </button>
                  ))}
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-600 bg-white rounded-xl border p-3">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    我理解这是关系实验，不是对未来的预测。对方倾向为资料推断，邀请共同实验可得真实作答。
                  </span>
                </label>
                <button
                  type="button"
                  disabled={busy || !targetId || !expKey}
                  onClick={() => startExperiment(expKey)}
                  className="w-full sm:w-auto px-6 py-3 rounded-full bg-violet-600 text-white font-semibold disabled:opacity-60"
                >
                  {busy ? "正在进入…" : "开始多轮关系实验"}
                </button>
              </>
            )}
            {!targetId && (
              <p className="text-xs text-amber-600">
                请先
                <button
                  type="button"
                  className="underline ml-1"
                  onClick={() => setPhase(PHASE.PICK)}
                >
                  选择对象
                </button>
              </p>
            )}
            <p className="text-[11px] text-gray-400">{disclaimer}</p>
          </section>
        )}

        {phase === PHASE.EXP_PLAY && expRound && (
          <section className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs text-violet-500">
                  多轮关系实验 · 第 {expRound.index}/{expRound.total} 轮
                </p>
                <h2 className="text-xl font-bold text-gray-900">
                  {expScenario?.title}
                </h2>
                {expScenario?.conflict_type && (
                  <p className="text-xs text-amber-700 mt-1">
                    冲突类型观察：{expScenario.conflict_type}
                  </p>
                )}
              </div>
              <div className="text-sm text-gray-500">
                {progress.step}/{progress.total}
              </div>
            </div>
            {expNote && (
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                {expNote}
              </p>
            )}
            {expRoundResult && (
              <div className="bg-white rounded-2xl border border-violet-100 p-4 space-y-2">
                <p className="text-xs text-violet-600 font-medium">
                  上一轮观察 · {expRoundResult.analysis?.tone || "对照"}
                </p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {expRoundResult.analysis?.summary ||
                    expRoundResult.analysis?.insight}
                </p>
                <div className="grid sm:grid-cols-2 gap-2 text-xs text-gray-600">
                  <p>
                    你：{expRoundResult.answer_a?.text || expRoundResult.answer_a?.key}
                  </p>
                  <p>
                    TA（推断）：
                    {expRoundResult.answer_b?.text || expRoundResult.answer_b?.key}
                    {expRoundResult.answer_b?.inferred ? " *" : ""}
                  </p>
                </div>
              </div>
            )}
            <div className="bg-white rounded-2xl border p-5 space-y-4">
              <p className="text-base font-medium text-gray-900 leading-relaxed">
                {expRound.question}
              </p>
              <div className="space-y-2">
                {(expRound.choices || []).map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    disabled={busy}
                    onClick={() => answerExperiment(c.key)}
                    className="w-full text-left px-4 py-3 rounded-xl border border-gray-100 hover:border-violet-300 hover:bg-violet-50 text-sm disabled:opacity-50"
                  >
                    <span className="font-semibold text-violet-700 mr-2">
                      {c.key}.
                    </span>
                    {c.text}
                  </button>
                ))}
              </div>
              <div className="pt-2 border-t space-y-2">
                <p className="text-xs text-gray-500">或写下你自己的回答</p>
                <textarea
                  value={expCustom}
                  onChange={(e) => setExpCustom(e.target.value.slice(0, 500))}
                  rows={2}
                  className="w-full border rounded-xl px-3 py-2 text-sm resize-none"
                  placeholder="用自己的话回答…"
                />
                <button
                  type="button"
                  disabled={busy || !expCustom.trim()}
                  onClick={() => answerExperiment("", expCustom)}
                  className="px-4 py-2 rounded-full bg-violet-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  {busy ? "提交中…" : "提交回答"}
                </button>
              </div>
            </div>
          </section>
        )}

        {phase === PHASE.EXP_REPORT && expReport && (
          <section className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <div>
              <p className="text-xs text-violet-500">关系实验报告</p>
              <h2 className="text-2xl font-bold text-gray-900">
                {expReport.title || "两个人的关系实验报告"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {expReport.score_note ||
                  "这不是预测，而是帮助你认识彼此的关系实验。"}
              </p>
            </div>
            <div className="bg-white rounded-2xl border p-5 space-y-4 text-sm text-gray-700">
              {[
                ["1. 发生了什么", expReport.what_happened || expReport.narrative_v3?.["1_what"]],
                ["2. 你可能怎么想", expReport.a_may_think || expReport.narrative_v3?.["2_a_think"]],
                ["3. TA 可能怎么想", expReport.b_may_think || expReport.narrative_v3?.["3_b_think"]],
                ["4. 真正的冲突点", expReport.real_conflict || expReport.narrative_v3?.["4_conflict"]],
                ["5. 最容易误解的地方", expReport.easy_misunderstand || expReport.narrative_v3?.["5_misunderstand"]],
              ].map(([label, text]) => (
                <div key={label}>
                  <p className="font-semibold text-gray-900">{label}</p>
                  <p className="mt-1 leading-relaxed text-gray-600">{text}</p>
                </div>
              ))}
              <div>
                <p className="font-semibold text-gray-900">6. 可以进一步讨论的问题</p>
                <ul className="mt-1 space-y-1 text-gray-600">
                  {(expReport.discuss_further || []).map((q) => (
                    <li key={q}>· {q}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-gray-900">7. 若继续交往建议观察什么</p>
                <ul className="mt-1 space-y-1 text-gray-600">
                  {(expReport.observe_if_dating || []).map((q) => (
                    <li key={q}>· {q}</li>
                  ))}
                </ul>
              </div>
            </div>
            {(expReport.round_analyses || expAnswers).length > 0 && (
              <div className="bg-violet-50/60 rounded-2xl border border-violet-100 p-4 space-y-3">
                <p className="text-sm font-semibold text-violet-900">逐轮对照</p>
                {(expReport.round_analyses || expAnswers).map((r, i) => (
                  <div key={r.round_id || i} className="text-xs text-gray-700 space-y-1">
                    <p className="font-medium">
                      第 {i + 1} 轮 · {r.tone || r.analysis?.tone || ""} ·{" "}
                      {r.question}
                    </p>
                    <p className="text-gray-500">
                      {r.insight || r.analysis?.insight}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <RelationshipFeedbackForm
              userId={userId}
              source="simulation"
              targetUserId={targetId}
              simulationId={expId}
              defaultTopic={
                (expScenario?.category === "work" && "work") ||
                expScenario?.category ||
                "general"
              }
              compact
            />
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setSceneTab("experiment");
                  setPhase(PHASE.SCENES);
                }}
                className="px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold"
              >
                再做一轮实验
              </button>
              <Link
                to="/ai/relationship-map"
                className="px-5 py-2.5 rounded-full border border-violet-200 text-violet-800 text-sm font-medium"
              >
                查看关系地图
              </Link>
              <button
                type="button"
                onClick={() => setPhase(PHASE.HERO)}
                className="px-5 py-2.5 rounded-full border text-sm"
              >
                返回首页
              </button>
            </div>
            <p className="text-[11px] text-gray-400">{disclaimer}</p>
          </section>
        )}

        {phase === PHASE.PLAY && turn && (
          <section className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs text-violet-500">
                  {scenario?.subtitle || "关系实验"} · Agent 进行中
                </p>
                <h2 className="text-xl font-bold text-gray-900">
                  {scenario?.title}
                </h2>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">
                  实验进度 {progress.step} / {progress.total}
                </p>
                <div className="w-28 h-1.5 bg-violet-100 rounded-full mt-1 overflow-hidden">
                  <div
                    className="h-full bg-violet-500"
                    style={{
                      width: `${Math.min(
                        100,
                        (Number(progress.step) / Number(progress.total || 6)) * 100
                      )}%`,
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setPhase(PHASE.SCENES)}
                  className="text-sm text-gray-500 mt-2"
                >
                  退出
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-violet-100 p-5 space-y-3 max-h-[40vh] overflow-y-auto">
              {story.map((line, i) => (
                <div
                  key={`${i}-${line.text?.slice(0, 12)}`}
                  className={`text-sm leading-relaxed ${
                    line.role === "me"
                      ? "text-right"
                      : line.role === "event"
                        ? "text-center text-gray-500 italic"
                        : "text-left"
                  }`}
                >
                  <span
                    className={`inline-block px-3 py-2 rounded-2xl ${
                      line.role === "me"
                        ? "bg-violet-600 text-white"
                        : line.role === "ta"
                          ? "bg-rose-50 text-rose-900"
                          : "bg-transparent"
                    }`}
                  >
                    {line.text}
                  </span>
                </div>
              ))}
            </div>

            {turn.partner_reaction && story[story.length - 1]?.role !== "ta" && (
              <div className="bg-rose-50/80 rounded-2xl p-4 text-sm text-rose-900 space-y-2">
                <p>{turn.partner_reaction}</p>
                {turn.why && (
                  <p className="text-xs text-rose-700/80">为什么可能这样：{turn.why}</p>
                )}
              </div>
            )}

            {(turn.ai_observe || turn.observation) && (
              <p className="text-xs text-gray-500 bg-white rounded-xl border px-3 py-2">
                🧠 AI 观察：{turn.ai_observe || turn.observation}
              </p>
            )}

            <div className="space-y-2">
              <p className="font-semibold text-gray-800">
                {turn.question || "现在你会怎么做？"}
              </p>
              <div className="space-y-2">
                {(turn.choices || []).map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    disabled={busy}
                    onClick={() => choose(c.key)}
                    className="w-full text-left px-4 py-3 rounded-2xl border border-gray-200 bg-white hover:border-violet-300 text-sm disabled:opacity-60"
                  >
                    <span className="font-semibold text-violet-600 mr-2">
                      {c.key}.
                    </span>
                    {c.text}
                  </button>
                ))}
              </div>
              <div className="bg-white rounded-2xl border p-3 space-y-2">
                <p className="text-xs text-gray-500">或自己回答</p>
                <textarea
                  value={customAnswer}
                  onChange={(e) => setCustomAnswer(e.target.value.slice(0, 500))}
                  rows={2}
                  placeholder="写下你会怎么做…"
                  className="w-full border rounded-xl px-3 py-2 text-sm resize-none"
                />
                <button
                  type="button"
                  disabled={busy || !customAnswer.trim()}
                  onClick={() => choose("", customAnswer)}
                  className="px-4 py-2 rounded-full bg-violet-600 text-white text-sm disabled:opacity-50"
                >
                  发送我的回答
                </button>
              </div>
            </div>
            <p className="text-[11px] text-gray-400">{disclaimer}</p>
          </section>
        )}

        {phase === PHASE.REPORT && report && (
          <section className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <h2 className="text-2xl font-bold text-gray-900">
              本次关系实验报告
            </h2>
            <p className="text-sm text-gray-500">
              {scenario?.title} · 你们经历了一段关系
              {report.events_played
                ? `（${report.events_played} 个互动事件）`
                : ""}
              ，这不是预测，而是帮助你认识彼此的关系实验。
            </p>

            {(report.state_curve || []).length > 0 && (
              <StateRadarCompare curve={report.state_curve} />
            )}

            {report.narrative_v3 && (
              <div className="bg-white rounded-2xl border p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-gray-800">本次关系观察（七段）</h3>
                  {report.focus && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">
                      焦点：{report.focus}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-400">
                  {report.score_note || "这是关系观察，不是匹配分数。"}
                </p>
                {[
                  ["1. 发生了什么", report.narrative_v3.what_happened],
                  ["2. 你可能怎么想", report.narrative_v3.a_might_think],
                  ["3. 对方可能怎么想", report.narrative_v3.b_might_think],
                  ["4. 真正的冲突点", report.narrative_v3.real_conflict],
                  ["5. 最容易误解的地方", report.narrative_v3.misunderstanding],
                  ["6. 可以进一步讨论", Array.isArray(report.narrative_v3.discuss_next)
                    ? report.narrative_v3.discuss_next.join("；")
                    : report.narrative_v3.discuss_next],
                  ["7. 若继续交往，建议观察", report.narrative_v3.observe_if_dating],
                ].map(([label, text]) =>
                  text ? (
                    <div key={label} className="text-sm">
                      <p className="text-xs font-semibold text-violet-700 mb-0.5">
                        {label}
                      </p>
                      <p className="text-gray-700 leading-relaxed">{text}</p>
                    </div>
                  ) : null
                )}
              </div>
            )}

            {report.pair_structure && (
              <div className="bg-violet-50/80 rounded-2xl border border-violet-100 p-4 space-y-2">
                <h3 className="font-semibold text-violet-950">两个人的关系结构</h3>
                <p className="text-[11px] text-violet-800/80">
                  {report.pair_structure.note}
                </p>
                {(report.pair_structure.resonate || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-emerald-800">容易共鸣</p>
                    <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                      {report.pair_structure.resonate.map((x) => (
                        <li key={x}>· {x}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(report.pair_structure.need_work || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-amber-800">需要磨合</p>
                    <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                      {report.pair_structure.need_work.map((x) => (
                        <li key={x}>· {x}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(report.pair_structure.conflict_risk || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-rose-800">潜在冲突区</p>
                    <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                      {report.pair_structure.conflict_risk.map((x) => (
                        <li key={x}>· {x}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(report.pair_structure.learn_more || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-sky-800">值得进一步了解</p>
                    <ul className="text-xs text-gray-700 mt-1 space-y-0.5">
                      {report.pair_structure.learn_more.map((x) => (
                        <li key={x}>· {x}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {(report.relationship_patterns || []).length > 0 && (
              <div className="bg-white rounded-2xl border p-4 space-y-2">
                <h3 className="font-semibold text-gray-800">
                  AI 发现了 {report.relationship_patterns.length} 个关系模式
                </h3>
                {report.relationship_patterns.map((p) => (
                  <div
                    key={p.title}
                    className="rounded-xl border px-3 py-2 text-sm"
                  >
                    <p className="font-medium text-gray-800">{p.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>
                  </div>
                ))}
              </div>
            )}

            {report.relationship_cycle && (
              <div className="bg-amber-50/80 rounded-2xl border border-amber-100 p-4 space-y-2">
                <h3 className="font-semibold text-amber-900">关系循环</h3>
                <div className="flex flex-wrap gap-1 text-xs text-amber-950">
                  {(report.relationship_cycle.steps || []).map((s, i) => (
                    <span key={`${s}-${i}`} className="inline-flex items-center">
                      {i > 0 && <span className="mx-1 text-amber-400">↓</span>}
                      <span className="rounded-full bg-white border px-2 py-0.5">
                        {s}
                      </span>
                    </span>
                  ))}
                </div>
                <p className="text-xs text-amber-900/90 leading-relaxed">
                  {report.relationship_cycle.note}
                </p>
              </div>
            )}

            <RelationshipFeedbackForm
              userId={userId}
              source="simulation"
              targetUserId={targetId}
              simulationId={simId}
              defaultTopic={
                scenario?.focus === "工作"
                  ? "work"
                  : scenario?.focus === "消费"
                    ? "money"
                    : scenario?.focus === "父母"
                      ? "parents"
                      : scenario?.focus === "孩子"
                        ? "children"
                        : scenario?.focus === "沟通"
                          ? "communication"
                          : scenario?.focus === "家庭分工"
                            ? "household"
                            : "general"
              }
              compact
            />

            <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-violet-50 p-4 space-y-2">
              <p className="text-sm font-semibold text-emerald-900">
                要不要练习一下？
              </p>
              <p className="text-xs text-emerald-900/80 leading-relaxed">
                {(report.relationship_patterns || []).find(
                  (p) => p.type === "watch"
                )?.desc ||
                  "AI 可以扮演伴侣，陪你练习换一种表达方式——把感受与需要说清楚。"}
              </p>
              <Link
                to={`/ai/relationship-train?sim=${simId || ""}`}
                className="inline-flex px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold"
              >
                开始关系训练
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border p-4">
                <h3 className="font-semibold text-emerald-700 mb-2">① 你们的优势</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {(report.strengths || []).map((s) => (
                    <li key={s}>· {s}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-2xl border p-4">
                <h3 className="font-semibold text-amber-700 mb-2">② 潜在差异</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {(report.differences || report.conflicts || []).map((s) => (
                    <li key={s}>· {s}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl border p-4 space-y-2">
              <h3 className="font-semibold text-gray-800">③ AI 观察到的模式</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{report.pattern}</p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-gray-800">④ 关系中的区域</h3>
              <div className="grid sm:grid-cols-3 gap-2">
                {(report.zones || []).map((z) => (
                  <ZoneCard key={z.title} zone={z} />
                ))}
              </div>
              <p className="text-[11px] text-gray-400">
                橙色表示「需要更多沟通」，不是「不能在一起」。
              </p>
            </div>

            {report.manual && (
              <div className="bg-gradient-to-br from-violet-50 to-rose-50 rounded-2xl border border-violet-100 p-5 space-y-3">
                <h3 className="font-bold text-violet-900">
                  💡 你们的关系使用说明书
                </h3>
                <dl className="space-y-2 text-sm text-gray-700">
                  <div>
                    <dt className="text-xs text-violet-500">第一件应该聊的事情</dt>
                    <dd>{report.manual.first_talk}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-violet-500">第一条可以建立的规则</dt>
                    <dd>{report.manual.first_rule}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-violet-500">最容易产生误解的地方</dt>
                    <dd>{report.manual.easy_conflict}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-violet-500">最值得珍惜的地方</dt>
                    <dd>{report.manual.treasure}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-violet-500">最值得观察的地方</dt>
                    <dd>{report.manual.watch}</dd>
                  </div>
                </dl>
                {(report.one_liners || []).length > 0 && (
                  <div className="pt-2 border-t border-violet-100 space-y-1">
                    {report.one_liners.map((line) => (
                      <p key={line} className="text-sm text-violet-800 italic">
                        “{line}”
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(report.discuss_first || []).length > 0 && (
              <div className="bg-white rounded-2xl border p-4">
                <h3 className="font-semibold text-gray-800 mb-2">
                  ⑤ 最值得提前讨论的问题
                </h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  {report.discuss_first.map((q) => (
                    <li key={q}>· {q}</li>
                  ))}
                </ul>
              </div>
            )}

            {(() => {
              const mapObj =
                report.relationship_map ||
                (relMap && relMap.map) ||
                (relMap && !relMap.items ? relMap : null);
              if (!mapObj || typeof mapObj !== "object") return null;
              const labels = {
                communication: "沟通",
                family: "家庭",
                money: "金钱",
                children: "孩子",
                emotion: "情绪",
                boundary: "边界",
                lifestyle: "生活方式",
                life_goal: "人生目标",
              };
              return (
                <div className="bg-white rounded-2xl border p-4 space-y-3">
                  <h3 className="font-semibold text-gray-800">你们的关系地图</h3>
                  <p className="text-[11px] text-gray-400">
                    根据本场实验生成的可视化观察，不是科学测量。
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(mapObj).map(([k, v]) => {
                      if (!labels[k]) return null;
                      return (
                        <div
                          key={k}
                          className="rounded-xl border px-3 py-2 flex flex-col gap-1"
                        >
                          <span className="text-xs text-gray-500">{labels[k]}</span>
                          <MapLevel level={v} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="rounded-2xl border border-violet-200 bg-violet-50/50 p-4 space-y-2">
              <p className="text-sm font-medium text-violet-900">
                邀请 TA 一起做关系实验
              </p>
              <p className="text-xs text-violet-700/90 leading-relaxed">
                可用场景题库，也可自定义题目。双方独立作答后 AI 对比。
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  disabled={busy || !targetId}
                  onClick={() => {
                    setJointMode("preset");
                    setShowJointComposer(true);
                    setPhase(PHASE.SCENES);
                  }}
                  className="px-4 py-2 rounded-full bg-violet-600 text-white text-xs font-semibold disabled:opacity-50"
                >
                  场景题库邀请
                </button>
                <button
                  type="button"
                  disabled={busy || !targetId}
                  onClick={() => {
                    setJointMode("custom_qs");
                    setShowJointComposer(true);
                    setPhase(PHASE.SCENES);
                  }}
                  className="px-4 py-2 rounded-full border border-violet-300 text-violet-700 text-xs font-semibold disabled:opacity-50"
                >
                  自定义题目邀请
                </button>
                <Link
                  to={
                    targetId
                      ? `/ai/relationship-growth?target=${targetId}`
                      : "/ai/relationship-growth"
                  }
                  className="px-4 py-2 rounded-full border text-gray-600 text-xs self-center"
                >
                  关系成长追踪
                </Link>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to={`/ai/relationship-train?sim=${simId || ""}`}
                className="px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold"
              >
                开始关系训练
              </Link>
              <button
                type="button"
                disabled={busy || !simId}
                onClick={retryLab}
                className="px-5 py-2.5 rounded-full border border-violet-300 text-violet-700 text-sm font-semibold disabled:opacity-50"
              >
                换一种方式再试一次
              </button>
              <button
                type="button"
                onClick={() => setPhase(PHASE.SCENES)}
                className="px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold"
              >
                再做一场实验
              </button>
              <button
                type="button"
                onClick={loadHistory}
                className="px-5 py-2.5 rounded-full border text-gray-600 text-sm"
              >
                查看实验历史
              </button>
              <Link
                to={targetId ? `/user/${targetId}` : "/discover"}
                className="px-5 py-2.5 rounded-full border border-rose-200 text-rose-600 text-sm font-medium"
              >
                查看 TA 的资料
              </Link>
              <Link
                to="/ai/growth"
                className="px-5 py-2.5 rounded-full border text-gray-600 text-sm"
              >
                去成长中心
              </Link>
            </div>
            <p className="text-[11px] text-gray-400 leading-relaxed">{disclaimer}</p>
          </section>
        )}

        {phase === PHASE.JOINT && jointSession && (
          <section className="space-y-5 animate-[fadeIn_0.3s_ease]">
            <button
              type="button"
              onClick={() => setPhase(PHASE.HERO)}
              className="text-sm text-gray-500"
            >
              ← 返回
            </button>
            <div>
              <p className="text-xs text-violet-500">双方共同实验 · Phase 2</p>
              <h2 className="text-2xl font-bold text-gray-900">
                {jointSession.scenario?.title || "共同关系实验"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {jointSession.inviter?.name || "我"} ×{" "}
                {jointSession.invitee?.name || "TA"}
                {jointSession.message ? ` · ${jointSession.message}` : ""}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={copyJointLink}
                className="px-4 py-2 rounded-full border border-violet-200 text-violet-700 text-xs"
              >
                {shareCopied ? "已复制链接" : "复制分享链接"}
              </button>
              <span className="text-[11px] text-gray-400">
                状态：
                {jointSession.status === "pending_accept"
                  ? "待对方接受"
                  : jointSession.status === "answering"
                    ? "作答中"
                    : jointSession.status === "done"
                      ? "已完成对比"
                      : jointSession.status}
              </span>
            </div>

            {jointSession.role === "invitee" &&
              jointSession.status === "pending_accept" && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
                  <p className="text-sm text-amber-900">
                    {jointSession.inviter?.name || "对方"}
                    邀请你一起做关系实验。你们会分别作答，完成后再看 AI 对比。
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={acceptJoint}
                      className="px-4 py-2 rounded-full bg-violet-600 text-white text-sm"
                    >
                      接受并开始
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={declineJoint}
                      className="px-4 py-2 rounded-full border text-sm"
                    >
                      暂时拒绝
                    </button>
                  </div>
                </div>
              )}

            {jointSession.role === "inviter" &&
              !jointSession.accepted &&
              jointSession.status !== "done" && (
                <p className="text-xs text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
                  已创建邀请。你可以先作答；对方接受并答完后才会生成对比报告。
                </p>
              )}

            {(jointSession.accepted ||
              jointSession.role === "inviter" ||
              jointSession.status === "done") &&
              jointSession.status !== "declined" && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    我的进度 {jointSession.progress?.answered || 0} /{" "}
                    {jointSession.progress?.total || 4}
                    {jointSession.partner_progress && (
                      <span className="text-gray-400 ml-2">
                        · 对方{" "}
                        {jointSession.partner_progress.answered || 0} /{" "}
                        {jointSession.partner_progress.total || 4}
                        {!jointSession.compare_report &&
                          "（对方答案完成前不会显示）"}
                      </span>
                    )}
                  </p>

                  {(jointSession.questions || []).map((q, idx) => (
                    <div
                      key={q.id}
                      className="bg-white rounded-2xl border p-4 space-y-3"
                    >
                      <p className="font-semibold text-gray-800 text-sm">
                        {idx + 1}. {q.text}
                      </p>
                      {q.my_answer ? (
                        <div className="text-sm space-y-1">
                          <p className="text-violet-700">
                            我的回答：{q.my_answer.text}
                          </p>
                          {q.partner_answer && (
                            <p className="text-rose-700">
                              TA 的回答：{q.partner_answer.text}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {(q.choices || []).map((c) => (
                            <button
                              key={c.key}
                              type="button"
                              disabled={busy}
                              onClick={() => answerJoint(q.id, c.key)}
                              className="w-full text-left px-3 py-2.5 rounded-xl border hover:border-violet-300 text-sm disabled:opacity-60"
                            >
                              <span className="text-violet-600 font-semibold mr-2">
                                {c.key}.
                              </span>
                              {c.text}
                            </button>
                          ))}
                          <div className="pt-1 space-y-2">
                            <textarea
                              rows={2}
                              value={jointCustomAns[q.id] || ""}
                              onChange={(e) =>
                                setJointCustomAns((prev) => ({
                                  ...prev,
                                  [q.id]: e.target.value.slice(0, 300),
                                }))
                              }
                              placeholder="或自己写回答…"
                              className="w-full border rounded-xl px-3 py-2 text-sm resize-none"
                            />
                            <button
                              type="button"
                              disabled={busy || !(jointCustomAns[q.id] || "").trim()}
                              onClick={() =>
                                answerJoint(q.id, "", jointCustomAns[q.id])
                              }
                              className="px-3 py-1.5 rounded-full bg-violet-600 text-white text-xs disabled:opacity-50"
                            >
                              提交我的回答
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

            {jointSession.compare_report && (
              <div className="space-y-4 border-t border-violet-100 pt-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {jointSession.compare_report.title || "AI 对比观察"}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {jointSession.compare_report.summary}
                </p>
                {(jointSession.compare_report.alignments || []).length > 0 && (
                  <div className="bg-emerald-50 rounded-2xl p-4">
                    <p className="font-semibold text-emerald-800 text-sm mb-2">
                      🟢 方向接近（{jointSession.compare_report.same_count}）
                    </p>
                    <ul className="text-xs text-emerald-900 space-y-2">
                      {jointSession.compare_report.alignments.map((a) => (
                        <li key={a.question}>
                          <span className="font-medium">{a.question}</span>
                          <br />
                          {a.note}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(jointSession.compare_report.differences || []).length > 0 && (
                  <div className="bg-amber-50 rounded-2xl p-4">
                    <p className="font-semibold text-amber-900 text-sm mb-2">
                      🟠 值得讨论（{jointSession.compare_report.diff_count}）
                    </p>
                    <ul className="text-xs text-amber-950 space-y-3">
                      {jointSession.compare_report.differences.map((d) => (
                        <li key={d.question}>
                          <p className="font-medium">{d.question}</p>
                          <p>我：{d.you}</p>
                          <p>TA：{d.partner}</p>
                          <p className="text-amber-800/80 mt-0.5">{d.note}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="bg-white rounded-2xl border p-4 text-sm space-y-2">
                  <p>
                    <span className="text-xs text-violet-500">第一件该聊的</span>
                    <br />
                    {jointSession.compare_report.first_talk}
                  </p>
                  <p>
                    <span className="text-xs text-violet-500">可建立的规则</span>
                    <br />
                    {jointSession.compare_report.first_rule}
                  </p>
                  {(jointSession.compare_report.one_liners || []).map((line) => (
                    <p key={line} className="italic text-violet-800">
                      “{line}”
                    </p>
                  ))}
                </div>
                {jointSession.relationship_map && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(jointSession.relationship_map).map(
                      ([k, v]) => {
                        const labels = {
                          communication: "沟通",
                          family: "家庭",
                          money: "金钱",
                          children: "孩子",
                          emotion: "情绪",
                          boundary: "边界",
                          lifestyle: "生活方式",
                          life_goal: "人生目标",
                        };
                        if (!labels[k]) return null;
                        return (
                          <div
                            key={k}
                            className="rounded-xl border px-3 py-2 flex flex-col gap-1"
                          >
                            <span className="text-xs text-gray-500">
                              {labels[k]}
                            </span>
                            <MapLevel level={v} />
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </div>
            )}

            <p className="text-[11px] text-gray-400">{disclaimer}</p>
          </section>
        )}

        {phase === PHASE.HISTORY && (
          <section className="space-y-4 animate-[fadeIn_0.3s_ease]">
            <button
              type="button"
              onClick={() => setPhase(PHASE.HERO)}
              className="text-sm text-gray-500"
            >
              ← 返回
            </button>
            <h2 className="text-2xl font-bold text-gray-900">我的关系实验</h2>
            <Link
              to={
                targetId
                  ? `/ai/relationship-growth?target=${targetId}`
                  : "/ai/relationship-growth"
              }
              className="inline-flex text-sm text-violet-700 underline"
            >
              打开关系成长追踪页 →
            </Link>
            <Link
              to="/ai/behavior-profile"
              className="inline-flex text-sm text-amber-800 underline ml-3"
            >
              我的关系行为画像 →
            </Link>
            {pairMemory && (
              <div className="bg-gradient-to-br from-violet-50 to-rose-50 rounded-2xl border border-violet-100 p-4 space-y-2">
                <h3 className="font-semibold text-sm text-violet-900">
                  与 {pairMemory.partner?.name || "TA"} 的关系记忆
                </h3>
                <p className="text-xs text-gray-600">
                  单人实验完成 {pairMemory.stats?.solo_done || 0} · 共同实验完成{" "}
                  {pairMemory.stats?.joint_done || 0}
                </p>
                {(pairMemory.memory_notes || []).length > 0 && (
                  <ul className="text-xs text-violet-800 space-y-1">
                    {pairMemory.memory_notes.map((n) => (
                      <li key={n}>· {n}</li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-gray-400">{pairMemory.note}</p>
              </div>
            )}
            {relMap?.items && (
              <div className="bg-white rounded-2xl border p-4 space-y-2">
                <h3 className="font-semibold text-sm">
                  与 {relMap.partner?.name || "TA"} 的关系地图
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {relMap.items.map((it) => (
                    <div
                      key={it.key}
                      className="flex items-center justify-between rounded-xl border px-3 py-2 text-xs"
                    >
                      <span>{it.label}</span>
                      <MapLevel level={it.level} />
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400">{relMap.note}</p>
              </div>
            )}
            {jointList.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-gray-800">共同实验</h3>
                {jointList.map((item) => (
                  <div
                    key={`j-${item.id}`}
                    className="bg-white rounded-2xl border border-violet-100 p-4 flex flex-wrap items-center justify-between gap-3"
                  >
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">
                        🤝 我 × {item.partner?.name || "TA"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {item.scenario?.title} ·{" "}
                        {item.status === "done"
                          ? "已完成对比"
                          : item.status === "pending_accept"
                            ? "待接受"
                            : "作答中"}
                        {item.progress
                          ? ` · 我 ${item.progress.answered}/${item.progress.total}`
                          : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openJoint(item.share_token)}
                      className="px-4 py-2 rounded-full border border-violet-200 text-violet-700 text-xs"
                    >
                      打开
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <h3 className="font-semibold text-sm text-gray-800">单人实验</h3>
              {historyItems.length === 0 ? (
                <p className="text-sm text-gray-500">还没有单人实验记录。</p>
              ) : (
                historyItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3"
                  >
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">
                        我 × {item.partner?.name || "TA"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {item.scenario?.title} ·{" "}
                        {item.status === "done" ? "已完成" : "进行中"}
                        {item.step
                          ? ` · ${item.step}/${item.total_steps || 6}`
                          : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => resumeLab(item.id)}
                      className="px-4 py-2 rounded-full border border-violet-200 text-violet-700 text-xs"
                    >
                      {item.status === "done" ? "查看报告" : "继续实验"}
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
